import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Inbox, ClipboardCheck, ArrowUpRight, Clock, MoonStar } from "lucide-react";
import { toast } from "sonner";
import ABExperimentPanel from "./ABExperimentPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_ORDER = [
  "Immediate Operational Risk",
  "Highly Person-Dependent",
  "Functional but Vulnerable",
  "Strong and Resilient",
];
const TIER_COLORS = {
  "Immediate Operational Risk": "#EF4444",
  "Highly Person-Dependent": "#F97316",
  "Functional but Vulnerable": "#D8A936",
  "Strong and Resilient": "#4ADE80",
};

const TYPE_LABEL = {
  recruitment: "Recruitment",
  consulting: "Consulting",
  ai_solutions: "AI Solutions",
  speaking: "Speaking",
  partnership: "Partnership",
  media: "Media",
  general: "General",
};

const SNOOZE_PRESETS = [
  { key: "tomorrow", label: "Tomorrow AM" },
  { key: "thursday", label: "Revisit Thursday" },
  { key: "next_monday", label: "Next Monday" },
  { key: "one_week", label: "In a week" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function WeeklyPulse() {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(null);
  const [snoozingId, setSnoozingId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/pulse?days=7`, { withCredentials: true })
      .then((r) => setPulse(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const snooze = async (id, preset) => {
    setSnoozingId(id);
    setMenuOpen(null);
    try {
      await axios.post(`${API}/admin/enquiries/${id}/snooze`, { preset }, { withCredentials: true });
      toast.success(`Snoozed · ${SNOOZE_PRESETS.find((p) => p.key === preset)?.label || preset}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snooze failed");
    } finally {
      setSnoozingId(null);
    }
  };

  if (error) {
    return (
      <section
        data-testid="weekly-pulse-error"
        className="border border-[#EF4444]/60 p-6 bg-[#151517] text-sm text-[#EF4444]"
      >
        Weekly pulse unavailable: {error}
      </section>
    );
  }
  if (!pulse) {
    return (
      <section
        data-testid="weekly-pulse-loading"
        className="border border-[#343437] p-6 bg-[#0B0B0C] text-sm text-[#A9A9A6]"
      >
        Loading this week…
      </section>
    );
  }

  const eq = pulse.enquiries;
  const dg = pulse.diagnostics;
  const enqTypeMax = Math.max(1, ...Object.values(eq.by_type || {}));

  return (
    <section
      data-testid="weekly-pulse"
      className="border border-[#D8A936]/60 bg-gradient-to-br from-[#151517] to-[#0B0B0C]"
      aria-labelledby="weekly-pulse-title"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 px-6 lg:px-8 pt-6 pb-4 border-b border-[#343437]">
        <div className="flex items-center gap-3">
          <Clock size={14} className="text-[#D8A936]" aria-hidden />
          <p className="eyebrow">This week · Last {pulse.days} days</p>
        </div>
        <p className="text-[10px] tracking-[0.18em] uppercase text-[#A9A9A6]">
          Generated {fmtDate(pulse.generated_at)}
        </p>
      </div>

      <h2 id="weekly-pulse-title" className="sr-only">
        Weekly pulse — enquiries and diagnostics from the last {pulse.days} days
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#343437]">
        {/* Enquiries */}
        <div className="bg-[#0B0B0C] p-6 lg:p-8" data-testid="weekly-pulse-enquiries">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#A9A9A6]">
              <Inbox size={14} />
              <span className="text-[10px] tracking-[0.22em] uppercase">Enquiries</span>
            </div>
            <Link
              to="/admin/enquiries"
              data-testid="weekly-pulse-enquiries-link"
              className="text-[10px] tracking-[0.18em] uppercase text-[#D8A936] hover:text-[#FAF9F6] flex items-center gap-1"
            >
              Inbox <ArrowUpRight size={12} />
            </Link>
          </div>
          <p
            className="oversize mt-4 text-5xl md:text-6xl text-[#D8A936]"
            data-testid="weekly-pulse-enquiries-count"
          >
            {eq.count}
          </p>
          <p className="mt-2 text-xs text-[#A9A9A6]">new enquiries this week</p>

          {Object.keys(eq.by_type || {}).length > 0 && (
            <ul className="mt-6 space-y-2">
              {Object.entries(eq.by_type).map(([k, v]) => {
                const label = TYPE_LABEL[k] || k;
                const pct = Math.round((v / enqTypeMax) * 100);
                return (
                  <li key={k} className="grid grid-cols-[110px_1fr_auto] gap-3 items-center text-xs">
                    <span className="text-[#A9A9A6]">{label}</span>
                    <div className="h-1.5 bg-[#151517] border border-[#343437]">
                      <div className="h-full bg-[#D8A936]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-display text-[#FAF9F6] w-6 text-right">{v}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {eq.recent.length > 0 && (
            <div className="mt-6 border-t border-[#343437] pt-4">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] mb-2">Most recent</p>
              <ul className="space-y-2 text-xs">
                {eq.recent.map((r) => (
                  <li
                    key={r.id}
                    className="relative flex items-baseline justify-between gap-3 group"
                    data-testid={`pulse-enquiry-row-${r.id}`}
                  >
                    <span className="text-[#FAF9F6] truncate">
                      {r.full_name || "Unknown"}
                      <span className="text-[#A9A9A6]"> · {TYPE_LABEL[r.enquiry_type] || r.enquiry_type}</span>
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6]">
                        {fmtDate(r.created_at)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setMenuOpen(menuOpen === r.id ? null : r.id)}
                        disabled={snoozingId === r.id}
                        aria-label={`Snooze ${r.full_name || "enquiry"}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen === r.id}
                        data-testid={`pulse-enquiry-snooze-${r.id}`}
                        className="p-1 text-[#A9A9A6] hover:text-[#D8A936] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      >
                        <MoonStar size={12} />
                      </button>
                    </div>
                    {menuOpen === r.id && (
                      <div
                        role="menu"
                        data-testid={`pulse-enquiry-snooze-menu-${r.id}`}
                        className="absolute right-0 top-full mt-1 z-20 border border-[#343437] bg-[#151517] shadow-2xl min-w-[180px]"
                      >
                        {SNOOZE_PRESETS.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            role="menuitem"
                            onClick={() => snooze(r.id, p.key)}
                            data-testid={`pulse-enquiry-snooze-${r.id}-${p.key}`}
                            className="block w-full text-left px-3 py-2 text-xs text-[#FAF9F6] hover:bg-[#0B0B0C] hover:text-[#D8A936] border-b border-[#343437] last:border-b-0"
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {eq.count === 0 && (
            <p className="mt-6 text-xs text-[#A9A9A6]" data-testid="weekly-pulse-enquiries-empty">
              — No new enquiries this week —
            </p>
          )}
        </div>

        {/* Diagnostics */}
        <div className="bg-[#0B0B0C] p-6 lg:p-8" data-testid="weekly-pulse-diagnostics">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#A9A9A6]">
              <ClipboardCheck size={14} />
              <span className="text-[10px] tracking-[0.22em] uppercase">Diagnostics</span>
            </div>
            <Link
              to="/admin/diagnostics"
              data-testid="weekly-pulse-diagnostics-link"
              className="text-[10px] tracking-[0.18em] uppercase text-[#D8A936] hover:text-[#FAF9F6] flex items-center gap-1"
            >
              All submissions <ArrowUpRight size={12} />
            </Link>
          </div>
          <p
            className="oversize mt-4 text-5xl md:text-6xl text-[#D8A936]"
            data-testid="weekly-pulse-diagnostics-count"
          >
            {dg.count}
          </p>
          <p className="mt-2 text-xs text-[#A9A9A6]">new diagnostic submissions</p>

          {Object.keys(dg.by_tier || {}).length > 0 && (
            <ul className="mt-6 space-y-2">
              {(() => {
                // Hoist once per render — was recomputed per-tier in the previous
                // version (micro-perf tidy flagged by code review).
                const tierMax = Math.max(1, ...Object.values(dg.by_tier));
                return TIER_ORDER.filter((t) => dg.by_tier[t]).map((tier) => {
                  const v = dg.by_tier[tier] || 0;
                  const pct = Math.round((v / tierMax) * 100);
                  const color = TIER_COLORS[tier] || "#D8A936";
                  return (
                    <li key={tier} className="grid grid-cols-[1fr_60px_auto] gap-3 items-center text-xs">
                      <span className="text-[#A9A9A6] truncate" title={tier}>
                        {tier}
                      </span>
                      <div className="h-1.5 bg-[#151517] border border-[#343437]">
                        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <span className="font-display text-[#FAF9F6] w-6 text-right">{v}</span>
                    </li>
                  );
                });
              })()}
            </ul>
          )}

          {dg.recent.length > 0 && (
            <div className="mt-6 border-t border-[#343437] pt-4">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] mb-2">Most recent</p>
              <ul className="space-y-2 text-xs">
                {dg.recent.map((r) => (
                  <li key={r.id} className="flex items-baseline justify-between gap-3">
                    <span className="text-[#FAF9F6] truncate">
                      {r.full_name || r.email || "Anonymous"}
                      <span className="text-[#A9A9A6]">
                        {" · "}
                        {r.score}/{r.max_score}
                      </span>
                    </span>
                    <span
                      className="text-[10px] tracking-[0.14em] uppercase shrink-0"
                      style={{ color: TIER_COLORS[r.tier] || "#A9A9A6" }}
                    >
                      {r.tier?.split(" ")[0] || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dg.count === 0 && (
            <p className="mt-6 text-xs text-[#A9A9A6]" data-testid="weekly-pulse-diagnostics-empty">
              — No new diagnostics this week —
            </p>
          )}
        </div>
      </div>

      <div className="mt-8">
        <ABExperimentPanel
          experiment="hero-role"
          eyebrow="A/B experiment · hero role line"
          title="Auto-promote the winning line"
        />
      </div>
      <div className="mt-8">
        <ABExperimentPanel
          experiment="primary-cta"
          eyebrow="A/B experiment · primary CTA label"
          title="Which CTA label converts best?"
          variantLabels={{
            A: "Work with me",
            B: "Get the CV",
            C: "Read the case studies",
          }}
        />
      </div>
    </section>
  );
}
